/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/Components/KubernetesCustomProviderComponent" />
import * as React from "react";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { localeFormat } from "VSS/Core/Util/String";

import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { Icon } from "VSSUI/Icon";
import { Button } from "VSSUI/Button";

import { IKubernetesCustomProviderData } from "Environments/ProviderCreateDetails/Kubernetes/Types";
import { safeParseJson, isUrlValid, copyToClipboard } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";

import * as Resources from "Environments/ProviderCreateDetails/Kubernetes/Resources";
import { publishTelemetry, FeatureNames } from "Environments/ProviderCreateDetails/Kubernetes/Telemetry";

interface IKubernetesCustomProviderState {
    serverUrlErrorMessage?: string;
    kubectlSecretErrorMessage?: string;
}

interface IKubernetesCustomProviderProps {
    instanceId: string;
    providerData: IKubernetesCustomProviderData;
    disableAll: boolean;
    isEditMode: boolean | undefined;
    onProviderDataChange: (data: IKubernetesCustomProviderData, isValid: boolean) => void;
}

export class KubernetesCustomProviderComponent extends VssComponent<IKubernetesCustomProviderProps, IKubernetesCustomProviderState> {
    constructor(props: IKubernetesCustomProviderProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            serverUrlErrorMessage: "",
            kubectlSecretErrorMessage: ""
        };
    }

    public render(): JSX.Element {

        return this._getNewServiceAccountContent();
    }

    private _getNewServiceAccountContent(): JSX.Element {
        return (
            <>
                <div className="cluster-section">
                    <h3 className="body-m font-weight-semibold">{Resources.ClusterCredentials}</h3>
                </div>

                <FormItem label={Resources.ClusterName} className="default-margin-top">
                    <TextField
                        value={this.props.providerData.clusterName}
                        onChange={this._onClusterChange}
                        disabled={this.props.disableAll || this.props.isEditMode}
                    />
                </FormItem>

                <FormItem label={Resources.Namespace} className="default-margin-top">
                    <TextField
                        value={this.props.providerData.kubernetesNamespace}
                        onChange={this._onKubernetesNamespaceChange}
                        disabled={this.props.disableAll || this.props.isEditMode}
                    />
                </FormItem>

                {
                    <div className={"default-margin-top"}>
                        <label htmlFor={"k8s-sa-serv-url"}>{Resources.ServerUrl}</label>

                        <div className={"sa-sugg-msg secondary-text"}>
                            {this._getSuggestionCommandMessage(Resources.ServerUrlSuggestionFormat, serverUrlCommand)}
                        </div>

                        <FormItem
                            error={!!this.state.serverUrlErrorMessage}
                            message={this.state.serverUrlErrorMessage}
                        >
                            <TextField
                                value={this.props.providerData.kubernetesServerUrl}
                                onChange={this._onServerUrlChange}
                                ariaLabel={Resources.ServerUrl}
                                required={true}
                                disabled={this.props.disableAll || this.props.isEditMode}
                                inputId="k8s-sa-serv-url"
                            />
                        </FormItem>
                    </div>
                }

                {
                    <div className={"default-margin-top"}>
                        <label htmlFor={"k8s-sa-creds"}>{Resources.KubectlSecretLabel}</label>

                        <div className={"sa-sugg-msg secondary-text"}>
                            <div className={"body-s sa-sugg-msg-text"}>{Resources.ServiceAccountSecretSuggestionHeading}</div>
                            {this._getSuggestionCommandMessage(Resources.ServiceAccountSuggestionFormat, serviceAccountCommand)}
                            {this._getSuggestionCommandMessage(Resources.ServiceAccountSecretSuggestionFormat, accountCredsCommand)}
                        </div>

                        <FormItem
                            error={!!this.state.kubectlSecretErrorMessage}
                            message={this.state.kubectlSecretErrorMessage}
                        >
                            <TextField
                                value={this.props.providerData.kubectlSecret}
                                onChange={this._onKubectlSecretChange}
                                ariaLabel={Resources.KubectlSecret}
                                multiline={true}
                                autoFocus={this.props.isEditMode}
                                disabled={this.props.disableAll}
                                inputId="k8s-sa-creds"
                            />
                        </FormItem>
                    </div>
                }

                <Checkbox
                    label={Resources.AcceptUntrustedCertificates}
                    checked={!!this.props.providerData.acceptUntrustedCertificates}
                    onChange={this._onAcceptUntrustedCertificatesChange}
                    disabled={this.props.disableAll}
                    className="default-margin-top no-padding"
                />
            </>
        );
    }

    private _onServerUrlChange = (e: any, value: string) => {
        let updatedData = Object.assign({}, this.props.providerData);
        updatedData.kubernetesServerUrl = value;
        if (!this.props.providerData.kubernetesServerUrl && !!value) {
            publishTelemetry(this.context.pageContext, FeatureNames.K8sSASurlFilled);
        }
        this.props.onProviderDataChange(updatedData, this._isValid(updatedData));
    }

    private _onKubectlSecretChange = (e: any, value: string) => {
        let updatedData = Object.assign({}, this.props.providerData);
        updatedData.kubectlSecret = value;
        if (!this.props.providerData.kubectlSecret && !!value) {
            publishTelemetry(this.context.pageContext, FeatureNames.K8sSASecretFilled);
        }

        this.props.onProviderDataChange(updatedData, this._isValid(updatedData));
    }

    private _onAcceptUntrustedCertificatesChange = (e: any, value: boolean) => {
        let updatedData = Object.assign({}, this.props.providerData);
        updatedData.acceptUntrustedCertificates = value;
        this.props.onProviderDataChange(updatedData, this._isValid(updatedData));
    }

    private _onClusterChange = (e: any, value: string) => {
        let updatedData = Object.assign({}, this.props.providerData);
        updatedData.clusterName = value;
        if (!this.props.providerData.clusterName && !!value) {
            publishTelemetry(this.context.pageContext, FeatureNames.K8sSAClusterFilled, {});
        }

        this.props.onProviderDataChange(updatedData, this._isValid(updatedData));
    }


    private _onKubernetesNamespaceChange = (e: any, value: string) => {
        let updatedData = Object.assign({}, this.props.providerData);
        updatedData.kubernetesNamespace = value;
        updatedData.serviceAccountName = this.props.providerData.environmentName + "-" + value + "-" + new Date().getTime().toString(); // TODO: raagra: Check with PM's on what should this name be. This will be the name of the endpoint.
        if (!this.props.providerData.kubernetesNamespace && !!value) {
            publishTelemetry(this.context.pageContext, FeatureNames.K8sSANamespaceFilled, {});
        }

        this.props.onProviderDataChange(updatedData, this._isValid(updatedData));
    }

    private _isValid(data: IKubernetesCustomProviderData): boolean {
        let valid = true;
        const isNamespaceValid = !!data.kubernetesNamespace;
        const isClusterValid = !!data.clusterName;

        const isServiceAccountNameValid = !!data.serviceAccountName;
        const isServerUrlValid = isUrlValid(data.kubernetesServerUrl);

        const kubectlSecretJson = safeParseJson(data.kubectlSecret);
        const isKubectlSecretValid = this._isKubectlSecretValid(kubectlSecretJson);

        valid = isServiceAccountNameValid && isServerUrlValid && isKubectlSecretValid && isNamespaceValid && isClusterValid;

        this.setState({
            // we should show error only when the input fields have some value
            serverUrlErrorMessage: !data.kubernetesServerUrl || isServerUrlValid ? "" : Resources.ServerURLError,
            kubectlSecretErrorMessage: !data.kubectlSecret || isKubectlSecretValid ? "" : localeFormat(Resources.KubectlSecretError, certificateField, tokenField)
        })

        return valid;
    }

    private _isKubectlSecretValid(kubectlSecretJson: any) {
        return kubectlSecretJson.data && kubectlSecretJson.data[certificateField] && kubectlSecretJson.data[tokenField];
    }

    private _getSuggestionCommandMessage(format: string, command: string): React.ReactNode {
        return (
            <FormatComponent format={format} className={"body-s sa-sugg-msg-text flex-row nowrap"} elementType={"div"} >
                <div className={"shell-command fontSizeS flex-row"}>
                    <div className={"overflow-ellipsis command-text"}>{command}</div>
                    <Button tooltipProps={{ text: Resources.CopyCommand }} onClick={() => copyToClipboard(command)} ariaLabel={Resources.CopyCommand}>
                        <Icon iconName={"Copy"} />
                    </Button>
                </div>
            </FormatComponent>
        );
    }
}

const certificateField = "ca.crt";
const tokenField = "token";
const serverUrlCommand = "kubectl config view --minify -o jsonpath={.clusters[0].cluster.server}";
const serviceAccountCommand = "kubectl get serviceAccounts <service-account-name> -n <namespace> -o=jsonpath={.secrets[*].name}";
const accountCredsCommand = "kubectl get secret <service-account-secret-name> -n <namespace> -o json";